import { useSyncExternalStore } from 'react';

/** The browser's offer to install the site as an app (Chrome, Edge, Android); Safari has none. */
interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let offer: InstallPrompt | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** Whether the terminal is already running as an installed app (its own window, no browser bar). */
export const runningInstalled = () =>
  typeof matchMedia === 'function' &&
  (matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true);

/**
 * Registers the service worker (in the built app only) and keeps the browser's install offer for the "Install app" menu
 * entry. Call once at start-up: the offer arrives early and only once per page load.
 */
export function startInstallSupport(production: boolean): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault(); // the terminal shows its own entry instead of the browser's banner
    offer = event as InstallPrompt;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    offer = null;
    notify();
  });
  if (production && 'serviceWorker' in navigator) {
    window.addEventListener(
      'load',
      () => void navigator.serviceWorker.register('/sw.js').catch(() => undefined),
    );
  }
}

export interface Install {
  /** The browser offers to install: show "Install app". */
  canInstall: boolean;
  /** iPhone or iPad Safari: installing is "Share, then Add to Home Screen". */
  iosHint: boolean;
  install(): Promise<void>;
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function useInstall(): Install {
  const current = useSyncExternalStore(subscribe, () => offer);
  const ios =
    typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !runningInstalled();
  return {
    canInstall: current !== null && !runningInstalled(),
    iosHint: ios,
    install: async () => {
      if (!offer) return;
      await offer.prompt();
      await offer.userChoice.catch(() => undefined);
      offer = null; // an offer can be used once
      notify();
    },
  };
}
